import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
// import { Link } from 'react-router-dom'; // 暂时未使用

// 页面容器样式
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 英雄区块样式
const HeroSection = styled('section', {
  position: 'relative',
  width: '100%',
  padding: '4rem 2rem',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, $accentPrimary 0%, $accentSecondary 100%)',
  color: 'white',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  
  '@media (max-width: 768px)': {
    padding: '3rem 1.5rem'
  }
});

// 背景形状样式
const BackgroundShapes = styled('div', {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  opacity: 0.1,
  zIndex: 0,
  background: 'url("/assets/wallet-bg-pattern.svg") center/cover no-repeat'
});

// 英雄内容样式
const HeroContent = styled('div', {
  position: 'relative',
  zIndex: 1,
  maxWidth: '800px'
});

// 标题样式
const Title = styled('h1', {
  fontSize: '3rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem',
  
  '@media (max-width: 768px)': {
    fontSize: '2.5rem'
  }
});

// 副标题样式
const Subtitle = styled('p', {
  fontSize: '1.25rem',
  lineHeight: 1.6,
  marginBottom: '2rem',
  opacity: 0.9,
  
  '@media (max-width: 768px)': {
    fontSize: '1.1rem'
  }
});

// 按钮容器样式
const ButtonContainer = styled('div', {
  display: 'flex',
  gap: '1rem',
  justifyContent: 'center',
  
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    width: '100%',
    maxWidth: '300px'
  }
});

// 按钮样式
const Button = styled('button', {
  padding: '0.875rem 2rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  
  variants: {
    variant: {
      primary: {
        background: 'white',
        color: '$accentPrimary',
        border: 'none',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 6px 15px rgba(0, 0, 0, 0.1)'
        }
      },
      secondary: {
        background: 'transparent',
        color: 'white',
        border: '2px solid white',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 6px 15px rgba(0, 0, 0, 0.1)'
        }
      }
    }
  },
  
  defaultVariants: {
    variant: 'primary'
  }
});

// 下载计数器样式
const DownloadCounter = styled('div', {
  marginTop: '2rem',
  fontSize: '0.9rem',
  opacity: 0.8
});

// 功能区块样式
const FeaturesSection = styled('section', {
  padding: '2rem 0'
});

// 区块标题样式
const SectionTitle = styled('h2', {
  fontSize: '2.25rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary',
  textAlign: 'center'
});

// 区块描述样式
const SectionDescription = styled('p', {
  fontSize: '1.1rem',
  color: '$textSecondary',
  marginBottom: '3rem',
  maxWidth: '800px',
  margin: '0 auto 3rem',
  textAlign: 'center',
  lineHeight: 1.6
});

// 功能卡片网格样式
const FeatureGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '2rem',
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr'
  }
});

// 功能卡片样式
const FeatureCard = styled('div', {
  background: '$backgroundSecondary',
  borderRadius: '12px',
  padding: '2rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  transition: 'transform 0.3s ease',
  
  '&:hover': {
    transform: 'translateY(-5px)'
  }
});

// 功能图标样式
const FeatureIcon = styled('div', {
  width: '64px',
  height: '64px',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, $accentPrimary 0%, $accentSecondary 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '2rem',
  color: 'white'
});

// 功能标题样式
const FeatureTitle = styled('h3', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '$textPrimary'
});

// 功能描述样式
const FeatureDescription = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 如何使用区块样式
const HowToSection = styled('section', {
  padding: '2rem 0'
});

// 步骤容器样式
const StepsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem'
});

// 步骤项样式
const StepItem = styled('div', {
  display: 'flex',
  gap: '1.5rem',
  alignItems: 'flex-start',
  
  '@media (max-width: 768px)': {
    flexDirection: 'column'
  }
});

// 步骤数字样式
const StepNumber = styled('div', {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, $accentPrimary 0%, $accentSecondary 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.25rem',
  fontWeight: 'bold',
  color: 'white',
  flexShrink: 0
});

// 步骤内容样式
const StepContent = styled('div', {
  flex: 1
});

// 步骤标题样式
const StepTitle = styled('h3', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  color: '$textPrimary'
});

// 步骤描述样式
const StepDescription = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 安全区块样式
const SecuritySection = styled('section', {
  padding: '3rem 2rem',
  background: '$backgroundSecondary',
  borderRadius: '16px',
  marginTop: '3rem'
});

// 安全项容器样式
const SecurityContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '2rem'
});

// 安全项样式
const SecurityItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
});

// 安全项标题样式
const SecurityItemTitle = styled('h4', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  color: '$textPrimary',
  display: 'flex',
  alignItems: 'center',
  gap: '0.75rem'
});

// 安全项描述样式
const SecurityItemDescription = styled('p', {
  fontSize: '0.95rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 支持的币种区块样式
const SupportedCoinsSection = styled('section', {
  padding: '2rem 0',
  textAlign: 'center'
});

// 币种图标网格样式
const CoinGrid = styled('div', {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '2rem',
  marginTop: '2rem'
});

// 币种图标项样式
const CoinItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.75rem'
});

// 币种图标样式
const CoinIcon = styled('div', {
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '$backgroundSecondary'
});

// 币种名称样式
const CoinName = styled('span', {
  fontSize: '0.9rem',
  color: '$textSecondary'
});

// 常见问题区块样式
const FaqSection = styled('section', {
  padding: '2rem 0',
  marginTop: '2rem'
});

// 问题答案容器样式
const FaqContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem'
});

// 问题答案项样式
const FaqItem = styled('div', {
  borderBottom: '1px solid $borderColor',
  paddingBottom: '1.5rem'
});

// 问题样式
const Question = styled('h4', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  marginBottom: '0.75rem',
  color: '$textPrimary'
});

// 答案样式
const Answer = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 行动召唤区块样式
const CtaSection = styled('section', {
  marginTop: '3rem',
  padding: '3rem 2rem',
  background: 'linear-gradient(135deg, $accentPrimary 0%, $accentSecondary 100%)',
  borderRadius: '16px',
  textAlign: 'center',
  color: 'white'
});

// 行动召唤标题样式
const CtaTitle = styled('h2', {
  fontSize: '2.25rem',
  fontWeight: 'bold',
  marginBottom: '1rem'
});

// 行动召唤描述样式
const CtaDescription = styled('p', {
  fontSize: '1.1rem',
  maxWidth: '700px',
  margin: '0 auto 2rem',
  opacity: 0.9,
  lineHeight: 1.6
});

/**
 * Kipa钱包概览页面组件
 * 介绍Kipa钱包的主要功能和特点
 */
const KipaWalletOverview: React.FC = () => {
  const { t } = useTranslation();
  
  // 功能数据
  const features = [
    {
      icon: '🔐',
      title: t('kipaWallet.features.security.title', '军事级安全防护'),
      description: t('kipaWallet.features.security.description', '采用最先进的加密技术和安全协议，保护您的数字资产安全，私钥完全由用户自己控制。')
    },
    {
      icon: '💱',
      title: t('kipaWallet.features.exchange.title', '内置交换功能'),
      description: t('kipaWallet.features.exchange.description', '无需离开钱包即可进行不同加密货币之间的交换，享受低手续费和优惠汇率。')
    },
    {
      icon: '📊',
      title: t('kipaWallet.features.portfolio.title', '资产组合管理'),
      description: t('kipaWallet.features.portfolio.description', '直观了解您的加密资产组合，跟踪投资绩效，查看实时市场数据和价格走势。')
    },
    {
      icon: '🌐',
      title: t('kipaWallet.features.dapps.title', 'DApp浏览器'),
      description: t('kipaWallet.features.dapps.description', '一键访问去中心化应用生态系统，包括NFT市场、DeFi服务、游戏等多种应用。')
    },
    {
      icon: '🔄',
      title: t('kipaWallet.features.multiChain.title', '多链支持'),
      description: t('kipaWallet.features.multiChain.description', '支持比特币、以太坊、BSC等多个区块链网络，一个钱包管理所有加密资产。')
    },
    {
      icon: '🚀',
      title: t('kipaWallet.features.staking.title', '一键质押'),
      description: t('kipaWallet.features.staking.description', '直接在钱包中质押您的加密货币，参与PoS共识机制，轻松赚取被动收益。')
    }
  ];
  
  // 步骤数据
  const steps = [
    {
      number: 1,
      title: t('kipaWallet.howTo.download.title', '下载钱包'),
      description: t('kipaWallet.howTo.download.description', '从官方网站或应用商店下载Kipa钱包应用，支持iOS、Android和桌面平台。')
    },
    {
      number: 2,
      title: t('kipaWallet.howTo.create.title', '创建钱包'),
      description: t('kipaWallet.howTo.create.description', '按照向导创建新钱包，或导入现有钱包。务必安全备份助记词，这是恢复钱包的唯一方式。')
    },
    {
      number: 3,
      title: t('kipaWallet.howTo.security.title', '设置安全措施'),
      description: t('kipaWallet.howTo.security.description', '设置强密码、生物识别认证和交易确认机制，提高钱包安全性。')
    },
    {
      number: 4,
      title: t('kipaWallet.howTo.deposit.title', '存入资产'),
      description: t('kipaWallet.howTo.deposit.description', '使用您的钱包地址接收加密货币，或直接在钱包内购买加密货币并存入。')
    },
    {
      number: 5,
      title: t('kipaWallet.howTo.explore.title', '探索功能'),
      description: t('kipaWallet.howTo.explore.description', '尝试钱包的交换、质押、浏览DApp等功能，开始您的加密货币之旅。')
    }
  ];
  
  // 安全特性数据
  const securityFeatures = [
    {
      icon: '🔒',
      title: t('kipaWallet.security.selfCustody.title', '自托管方案'),
      description: t('kipaWallet.security.selfCustody.description', '您完全掌控私钥，只有您能访问您的资产，第三方无法获取或冻结。')
    },
    {
      icon: '🛡️',
      title: t('kipaWallet.security.encryption.title', '端到端加密'),
      description: t('kipaWallet.security.encryption.description', '所有通信和数据都使用军事级加密技术保护，防止中间人攻击和数据泄露。')
    },
    {
      icon: '👆',
      title: t('kipaWallet.security.biometric.title', '生物识别认证'),
      description: t('kipaWallet.security.biometric.description', '支持指纹和面部识别，确保只有设备所有者能够访问钱包。')
    },
    {
      icon: '🔍',
      title: t('kipaWallet.security.audit.title', '定期安全审计'),
      description: t('kipaWallet.security.audit.description', '钱包代码由独立安全机构定期审计，确保没有安全漏洞和风险。')
    }
  ];
  
  // 支持的币种数据
  const supportedCoins = [
    { icon: '₿', name: 'Bitcoin (BTC)' },
    { icon: 'Ξ', name: 'Ethereum (ETH)' },
    { icon: 'B', name: 'BNB Chain (BNB)' },
    { icon: 'ü', name: 'Solana (SOL)' },
    { icon: 'Ð', name: 'Dogecoin (DOGE)' },
    { icon: '₳', name: 'Cardano (ADA)' },
    { icon: 'Ł', name: 'Litecoin (LTC)' },
    { icon: '⚫', name: 'Polkadot (DOT)' },
    { icon: '+', name: '更多100+币种' }
  ];
  
  // 常见问题数据
  const faqs = [
    {
      question: t('kipaWallet.faq.secure.question', 'Kipa钱包安全吗？'),
      answer: t('kipaWallet.faq.secure.answer', 'Kipa钱包采用先进的安全协议和加密技术，您的私钥仅存储在本地设备，且经过加密。我们支持多种安全认证方式，包括指纹、面容ID和密码保护。我们定期进行安全审计，确保钱包的安全性。')
    },
    {
      question: t('kipaWallet.faq.fees.question', 'Kipa钱包有哪些费用？'),
      answer: t('kipaWallet.faq.fees.answer', 'Kipa钱包的基本功能是免费的，包括创建钱包、接收和发送加密货币。交易时您需要支付网络矿工费，这是区块链网络收取的费用，不是Kipa钱包收取的。交换和质押功能收取少量服务费，我们保证收费透明，没有隐藏费用。')
    },
    {
      question: t('kipaWallet.faq.recovery.question', '如果我丢失手机，如何恢复我的钱包？'),
      answer: t('kipaWallet.faq.recovery.answer', '只要您安全备份了助记词，您可以在任何设备上恢复钱包。在新设备上下载Kipa钱包，选择"恢复钱包"，输入您的助记词即可。这就是为什么我们强烈建议您在创建钱包时安全备份助记词。')
    },
    {
      question: t('kipaWallet.faq.difference.question', 'Kipa钱包与其他加密钱包有什么不同？'),
      answer: t('kipaWallet.faq.difference.answer', 'Kipa钱包专为BestMine平台用户设计，提供与我们挖矿服务的无缝集成，同时兼具通用加密钱包的所有功能。我们提供更智能的用户界面、更低的交易费用，以及针对挖矿收益的优化管理功能。')
    }
  ];
  
  return (
    <PageContainer>
      <HeroSection>
        <BackgroundShapes />
        <HeroContent>
          <Title>{t('kipaWallet.hero.title', 'Kipa钱包 - BestMine的数字资产管家')}</Title>
          <Subtitle>{t('kipaWallet.hero.subtitle', '安全、便捷、功能丰富的自托管钱包，为您的加密货币资产提供全方位保护')}</Subtitle>
          <ButtonContainer>
            <Button variant="primary">{t('kipaWallet.hero.download', '立即下载')}</Button>
            <Button variant="secondary">{t('kipaWallet.hero.learnMore', '了解更多')}</Button>
          </ButtonContainer>
          <DownloadCounter>{t('kipaWallet.hero.downloads', '全球已有100,000+用户')}</DownloadCounter>
        </HeroContent>
      </HeroSection>
      
      <FeaturesSection>
        <SectionTitle>{t('kipaWallet.features.title', '钱包功能')}</SectionTitle>
        <SectionDescription>{t('kipaWallet.features.description', '全面的功能满足您所有加密货币管理需求')}</SectionDescription>
        
        <FeatureGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeatureGrid>
      </FeaturesSection>
      
      <HowToSection>
        <SectionTitle>{t('kipaWallet.howTo.title', '如何使用')}</SectionTitle>
        <SectionDescription>{t('kipaWallet.howTo.description', '简单几步设置和开始使用您的Kipa钱包')}</SectionDescription>
        
        <StepsContainer>
          {steps.map((step, index) => (
            <StepItem key={index}>
              <StepNumber>{step.number}</StepNumber>
              <StepContent>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepContent>
            </StepItem>
          ))}
        </StepsContainer>
      </HowToSection>
      
      <SecuritySection>
        <SectionTitle>{t('kipaWallet.security.title', '安全至上')}</SectionTitle>
        <SectionDescription>{t('kipaWallet.security.description', '我们采用多层次安全架构保护您的数字资产')}</SectionDescription>
        
        <SecurityContainer>
          {securityFeatures.map((feature, index) => (
            <SecurityItem key={index}>
              <SecurityItemTitle>
                <span>{feature.icon}</span> {feature.title}
              </SecurityItemTitle>
              <SecurityItemDescription>{feature.description}</SecurityItemDescription>
            </SecurityItem>
          ))}
        </SecurityContainer>
      </SecuritySection>
      
      <SupportedCoinsSection>
        <SectionTitle>{t('kipaWallet.coins.title', '支持的加密货币')}</SectionTitle>
        <SectionDescription>{t('kipaWallet.coins.description', '我们支持所有主流加密货币和众多代币')}</SectionDescription>
        
        <CoinGrid>
          {supportedCoins.map((coin, index) => (
            <CoinItem key={index}>
              <CoinIcon>{coin.icon}</CoinIcon>
              <CoinName>{coin.name}</CoinName>
            </CoinItem>
          ))}
        </CoinGrid>
      </SupportedCoinsSection>
      
      <FaqSection>
        <SectionTitle>{t('kipaWallet.faq.title', '常见问题')}</SectionTitle>
        <SectionDescription>{t('kipaWallet.faq.description', '关于Kipa钱包的常见问题解答')}</SectionDescription>
        
        <FaqContainer>
          {faqs.map((faq, index) => (
            <FaqItem key={index}>
              <Question>{faq.question}</Question>
              <Answer>{faq.answer}</Answer>
            </FaqItem>
          ))}
        </FaqContainer>
      </FaqSection>
      
      <CtaSection>
        <CtaTitle>{t('kipaWallet.cta.title', '开始您的加密之旅')}</CtaTitle>
        <CtaDescription>{t('kipaWallet.cta.description', '立即下载Kipa钱包，安全管理您的数字资产，享受加密世界的便利。')}</CtaDescription>
        <ButtonContainer>
          <Button variant="primary">{t('kipaWallet.cta.download', '立即下载')}</Button>
          <Button variant="secondary">{t('kipaWallet.cta.contact', '联系我们')}</Button>
        </ButtonContainer>
      </CtaSection>
    </PageContainer>
  );
};

export default KipaWalletOverview; 